import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home as HomeIcon, Building2, Sun, LayoutGrid, TrendingUp, ShieldCheck,
  Paintbrush, Headset, Award, CheckCircle2, ChevronLeft, ChevronRight,
  MapPin, ArrowRight,
} from "lucide-react";
import { IMG } from "@/lib/images";
import { Reveal } from "@/components/Reveal";
import { SectionHeading, BTN_PRIMARY, BTN_OUTLINE, BTN_WHITE } from "@/components/Shared";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { useQuote } from "@/context/QuoteContext";

const services = [
  { icon: HomeIcon, title: "RESIDENTIAL", img: IMG.res1, text: "Safe, streak-free cleaning using purified water for maximum performance." },
  { icon: Building2, title: "COMMERCIAL", img: IMG.comm1, text: "Reliable scheduled maintenance for businesses, churches, schools, warehouses, and office buildings." },
  { icon: Sun, title: "UTILITY SCALE", img: IMG.farm1, text: "Professional cleaning solutions for utility-scale solar farms.", note: "Pure water cleaning available upon request." },
];

const whyReasons = [
  { icon: TrendingUp, title: "MORE ENERGY", text: "Dirt and debris reduce efficiency." },
  { icon: ShieldCheck, title: "PROTECT YOUR INVESTMENT", text: "Routine cleaning helps preserve system performance." },
  { icon: Paintbrush, title: "PROFESSIONAL EQUIPMENT", text: "Soft-bristle brushes and industry-approved methods." },
  { icon: Headset, title: "RELIABLE SERVICE", text: "Professional, dependable scheduling and communication." },
];

const difference = [
  { label: "RESIDENTIAL", img: IMG.res3 },
  { label: "COMMERCIAL", img: IMG.comm2 },
  { label: "SOLAR FARMS", img: IMG.farm4 },
];

const stats = [
  { icon: LayoutGrid, value: "70,000+", title: "SOLAR PANELS CLEANED", sub: "+ AND COUNTING" },
  { icon: HomeIcon, value: "RESIDENTIAL", title: "HOMEOWNERS SERVED" },
  { icon: Building2, value: "COMMERCIAL", title: "BUSINESS PROJECTS" },
  { icon: Sun, value: "UTILITY", title: "LARGE SCALE SOLAR FARMS" },
];

const pins = [
  { top: "34%", left: "58%" }, { top: "44%", left: "48%" }, { top: "40%", left: "66%" },
  { top: "52%", left: "56%" }, { top: "58%", left: "44%" }, { top: "64%", left: "60%" },
  { top: "72%", left: "52%" },
];

export default function Home() {
  const carouselRef = useRef(null);
  const { openQuote } = useQuote();

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="relative h-[300px] w-full md:absolute md:right-0 md:top-0 md:h-full md:w-[48%]">
          <img src={IMG.hero} alt="Solar panels at golden hour" className="h-full w-full object-cover" />
          <div className="absolute inset-y-0 left-0 hidden w-1/2 bg-gradient-to-r from-white via-white/60 to-transparent md:block" />
        </div>
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <div className="max-w-[560px] py-12 md:py-24">
            <h1 className="font-display text-5xl font-extrabold leading-[1.04] tracking-tight text-[#0A2342] sm:text-6xl lg:text-7xl">
              PROFESSIONAL SOLAR PANEL CLEANING
            </h1>
            <p className="mt-5 font-display text-[15px] font-bold uppercase tracking-[0.08em] text-[#1E5EDB] sm:text-lg">
              Residential · Commercial · Utility-Scale Solar Farms
            </p>
            <div className="mt-6 space-y-4 text-[16px] leading-relaxed text-[#5B6B7F]">
              <p>Increase energy production, protect your investment, and extend the life of your solar array with professional solar panel cleaning services.</p>
              <p>Serving residential homes, commercial properties, and utility-scale solar farms throughout Texas.</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <button onClick={openQuote} data-testid="hero-quote-btn" className={BTN_PRIMARY}>GET A QUOTE</button>
              <Link to="/services" data-testid="hero-services-btn" className={BTN_OUTLINE}>OUR SERVICES</Link>
            </div>
          </div>
        </div>
      </section>

      {/* BY THE NUMBERS */}
      <section className="bg-[#F4F7FB]">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8 py-10">
          <div className="grid items-center gap-8 lg:grid-cols-[190px_1fr]">
            <div>
              <p className="font-display text-lg font-extrabold text-[#0A2342]">BY THE NUMBERS</p>
              <span className="mt-2 block h-1 w-12 rounded-full bg-[#1E5EDB]" />
            </div>
            <div className="grid grid-cols-2 gap-y-8 md:grid-cols-4">
              {stats.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.08} className={`flex flex-col items-center text-center ${i !== 0 ? "md:border-l md:border-[#D9E2EE]" : ""} px-2`}>
                  <s.icon size={34} className="text-[#1E5EDB]" strokeWidth={1.8} />
                  <p className="mt-2 font-display text-2xl font-extrabold text-[#0A2342]">{s.value}</p>
                  <p className="mt-1 text-[12px] font-semibold uppercase tracking-wide text-[#5B6B7F]">{s.title}</p>
                  {s.sub && <p className="text-[11px] font-medium text-[#8A99AB]">{s.sub}</p>}
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OUR SERVICES */}
      <section className="mx-auto max-w-[1200px] px-5 lg:px-8 py-20">
        <Reveal><SectionHeading title="OUR SERVICES" /></Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div className="group h-full overflow-hidden rounded-2xl bg-white shadow-[0_12px_40px_-18px_rgba(10,35,66,0.35)] transition-transform duration-300 hover:-translate-y-1.5">
                <div className="relative">
                  <img src={s.img} alt={s.title} className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute -bottom-6 left-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#1E5EDB] shadow-lg">
                    <s.icon size={26} className="text-white" />
                  </span>
                </div>
                <div className="px-6 pb-8 pt-10">
                  <h3 className="font-display text-xl font-extrabold text-[#0A2342]">{s.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#5B6B7F]">
                    {s.text}{" "}
                    {s.note && <span className="font-semibold italic text-[#1E5EDB]">{s.note}</span>}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CLEAN */}
      <section className="bg-[#F4F7FB]">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8 py-16">
          <div className="grid items-start gap-10 lg:grid-cols-[240px_1fr]">
            <div>
              <h2 className="font-display text-3xl font-extrabold leading-tight text-[#0A2342]">WHY CLEAN SOLAR PANELS?</h2>
              <span className="mt-4 block h-1 w-14 rounded-full bg-[#1E5EDB]" />
            </div>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {whyReasons.map((r, i) => (
                <Reveal key={r.title} delay={i * 0.08} className={`${i !== 0 ? "lg:border-l lg:border-[#D9E2EE] lg:pl-6" : ""}`}>
                  <r.icon size={34} className="text-[#1E5EDB]" strokeWidth={1.7} />
                  <h3 className="mt-3 font-display text-[15px] font-extrabold text-[#0A2342]">{r.title}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-[#5B6B7F]">{r.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEE THE DIFFERENCE */}
      <section className="mx-auto max-w-[1200px] px-5 lg:px-8 py-20">
        <Reveal><SectionHeading title="SEE THE DIFFERENCE" /></Reveal>
        <div className="relative mt-14">
          <button onClick={() => scrollCarousel(-1)} data-testid="diff-prev" aria-label="Previous" className="absolute -left-2 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white p-2 shadow-md hover:bg-[#F4F7FB] lg:flex">
            <ChevronLeft className="text-[#0A2342]" />
          </button>
          <button onClick={() => scrollCarousel(1)} data-testid="diff-next" aria-label="Next" className="absolute -right-2 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white p-2 shadow-md hover:bg-[#F4F7FB] lg:flex">
            <ChevronRight className="text-[#0A2342]" />
          </button>
          <div ref={carouselRef} className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {difference.map((d) => (
              <div key={d.label} className="min-w-[85%] snap-center sm:min-w-[46%] lg:min-w-[calc(33.333%-1rem)]">
                <BeforeAfterSlider image={d.img} label={d.label} testid={`diff-slider-${d.label.toLowerCase().replace(/\s+/g, "-")}`} className="h-56" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCE YOU CAN TRUST */}
      <section className="grid lg:grid-cols-2">
        <div className="h-[300px] lg:h-auto">
          <img src={IMG.truck} alt="Solar Pros company truck and equipment" className="h-full w-full object-cover" />
        </div>
        <div className="bg-white px-5 py-14 lg:px-14">
          <div className="grid gap-10 md:grid-cols-[1fr_190px]">
            <Reveal>
              <h2 className="font-display text-3xl font-extrabold text-[#0A2342]">EXPERIENCE YOU CAN TRUST</h2>
              <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-[#5B6B7F]">
                <p>Over 70,000 solar panels cleaned and counting, from residential rooftops to utility-scale solar farms.</p>
                <p>We understand the different cleaning requirements for every type of solar installation and tailor our services to each project.</p>
              </div>
              <Link to="/about" data-testid="home-about-btn" className={`${BTN_PRIMARY} mt-7`}>LEARN MORE ABOUT US</Link>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="space-y-6 rounded-2xl bg-[#F4F7FB] p-6">
                {[{ icon: Award, label: "EXPERIENCED PROFESSIONALS" }, { icon: ShieldCheck, label: "FULLY INSURED" }, { icon: CheckCircle2, label: "SAFETY FOCUSED" }].map((b) => (
                  <div key={b.label} className="flex items-center gap-3">
                    <b.icon size={26} className="shrink-0 text-[#1E5EDB]" />
                    <span className="font-display text-[13px] font-bold leading-tight text-[#0A2342]">{b.label}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVING TEXAS + FINAL CTA */}
      <section className="grid lg:grid-cols-2">
        <div className="bg-white px-5 py-14 lg:px-14">
          <h2 className="font-display text-3xl font-extrabold text-[#0A2342]">SERVING TEXAS</h2>
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[{ icon: HomeIcon, label: "RESIDENTIAL" }, { icon: Building2, label: "COMMERCIAL" }, { icon: Sun, label: "UTILITY SOLAR" }].map((m) => (
              <div key={m.label} className="flex flex-col items-center text-center">
                <m.icon size={30} className="text-[#1E5EDB]" strokeWidth={1.8} />
                <span className="mt-2 font-display text-[12px] font-bold text-[#0A2342]">{m.label}</span>
              </div>
            ))}
          </div>
          <div className="relative mx-auto mt-8 w-full max-w-[360px]">
            <img src={IMG.texas} alt="Texas service area map" className="w-full opacity-90" />
            {pins.map((p, i) => (
              <MapPin key={i} size={22} className="absolute text-[#1E5EDB]" style={{ top: p.top, left: p.left }} fill="#1E5EDB" strokeWidth={1} />
            ))}
          </div>
        </div>
        <div className="flex flex-col justify-center bg-[#1E5EDB] px-5 py-16 text-white lg:px-14">
          <Sun size={56} className="text-white/90" strokeWidth={1.5} />
          <h3 className="mt-5 font-display text-3xl font-extrabold leading-tight sm:text-4xl">READY TO MAXIMIZE YOUR SOLAR INVESTMENT?</h3>
          <p className="mt-3 text-white/85">Request your free quote today.</p>
          <button onClick={openQuote} data-testid="home-final-cta" className={`${BTN_WHITE} mt-7 w-fit`}>
            GET A QUOTE <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
}
